import { useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from '@/animations/gsap';
import { useLang } from '@/i18n/LanguageContext';
import { EASE } from '@/components/MaskLines';

export default function Verbs() {
  const { t } = useLang();
  const wrapRef = useRef(null);
  const wordRefs = useRef([]);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      const words = wordRefs.current.filter(Boolean);
      if (!words.length || !wrapRef.current) return;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapRef.current,
          start: 'top top',
          end: '+=320%',
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });
      t.verbs.forEach((v, i) => {
        const el = words[i];
        const fromX = i % 2 === 0 ? -110 : 110;
        tl.fromTo(el, { xPercent: fromX, autoAlpha: 0 }, { xPercent: 0, autoAlpha: 1, duration: 1.1, ease: 'power3.out' }, i * 3);
        if (i < t.verbs.length - 1) {
          tl.to(el, { xPercent: i % 2 === 0 ? 34 : -34, autoAlpha: 0, duration: 0.7, ease: 'power2.in' }, i * 3 + 2.3);
        }
      });
      tl.to({}, { duration: 0.8 });
      return () => tl.kill();
    });
    return () => mm.revert();
  }, [t]);

  return (
    <section data-testid="section-verbs" className="relative" aria-label="Raccontare. Emozionare. Convincere.">
      {/* desktop: pinned cinematic sequence */}
      <div ref={wrapRef} className="relative hidden h-screen overflow-hidden lg:block">
        {t.verbs.map((v, i) => (
          <div
            key={v.id}
            ref={(el) => (wordRefs.current[i] = el)}
            data-testid={`verb-${v.id}-desktop`}
            className="absolute inset-0 flex flex-col justify-center px-10 xl:px-16 opacity-0"
          >
            <p className={`font-display text-[9.5vw] leading-[0.86] ${i === 2 ? 'text-[var(--pz-green)]' : ''}`}>{v.word}</p>
            <p className="mt-8 font-mono-label text-white/50">{v.def}</p>
          </div>
        ))}
      </div>

      {/* mobile: stacked reveals */}
      <div className="lg:hidden">
        {t.verbs.map((v, i) => (
          <div key={v.id} className="flex min-h-[62vh] flex-col justify-center px-5 py-10" data-testid={`verb-${v.id}-mobile`}>
            <motion.p
              initial={{ x: i % 2 === 0 ? -40 : 40, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.8, ease: EASE }}
              className={`font-display text-[15vw] leading-[0.86] md:text-[9.5vw] ${i === 2 ? 'text-[var(--pz-green)]' : ''}`}
            >
              {v.word}
            </motion.p>
            <p className="mt-6 font-mono-label text-white/50">{v.def}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

